import { Link, useParams } from 'react-router-dom';
import { useProjectView } from '@/hooks/useProjectViews';
import { Button, Card, CardContent, Spinner } from '@/components/ui';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import {
  executionMilestones,
  milestoneBudget,
} from '@/lib/project-presentation';

export default function PaymentFundPage() {
  const { id } = useParams<{ id: string }>();
  const { currency } = useDaoConfig();
  const query = useProjectView(id);
  if (id && query.isPending)
    return (
      <p role="status" className="pageContent">
        <Spinner /> Loading project…
      </p>
    );
  if (id && query.isError)
    return (
      <div className="pageContent">
        <p role="alert">{query.error.message}</p>
        <Button onClick={() => void query.refetch()}>Retry</Button>
      </div>
    );
  const project = query.data;
  const milestones = project ? executionMilestones(project) : [];
  const total = milestones
    .reduce((sum, m) => sum + BigInt(milestoneBudget(m)), 0n)
    .toString();
  return (
    <div className="min-h-screen bg-[--base-surface-1] px-8 py-10 text-left lg:px-14">
      <nav
        className="mb-6 text-sm text-[--text-dark-secondary]"
        aria-label="Breadcrumb"
      >
        <Link to="/payments">Payments</Link>
        <span className="mx-2">/</span>
        {project && (
          <>
            <Link to={'/projects/' + project.projectId}>{project.title}</Link>
            <span className="mx-2">/</span>
          </>
        )}
        <span className="text-[--text-dark-primary]">Deposit</span>
      </nav>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <Card className="self-start rounded-[12px] border-[#3d3d3d] bg-[#231f1f]">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold">Project Milestones</h2>
            <p className="mt-1 text-sm text-[--text-dark-secondary]">
              You need tokens to fund this project.
            </p>
            <div className="mt-5">
              {!milestones.length && (
                <p className="py-4 text-center text-sm text-[--text-dark-secondary]">
                  No milestones defined yet.
                </p>
              )}
              {milestones.map((m, index) => (
                <div
                  key={m.milestoneId}
                  className={
                    'flex items-center justify-between py-3 ' +
                    (index < milestones.length - 1
                      ? 'border-b border-[#3d3d3d]'
                      : '')
                  }
                >
                  <div className="min-w-0 flex-1">
                    <p className="mb-0.5 text-xs text-[rgba(255,255,255,0.4)]">
                      Milestone {index + 1}
                    </p>
                    <p className="truncate text-sm text-[#f5f5f5]">
                      {m.definition.title}
                    </p>
                  </div>
                  <span className="ml-4 shrink-0 text-sm font-semibold text-[#f5f5f5]">
                    {milestoneBudget(m)} {currency.symbol}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-between rounded-[8px] border border-[#3d3d3d] px-4 py-3">
              <span className="text-sm font-semibold">Total</span>
              <span className="font-bold text-[#36d399]">{total} {currency.symbol}</span>
            </div>
          </CardContent>
        </Card>
        <div>
          <h2 className="mb-4 text-lg font-semibold">
            Deposit via Bank Transfer
          </h2>
          <Card className="rounded-[12px] border-[#3d3d3d] bg-[#231f1f]">
            <CardContent className="space-y-5 p-6">
              <div className="space-y-3 rounded-[8px] border border-[#3d3d3d] p-4">
                <div className="flex justify-between">
                  <span className="text-xs text-[--text-dark-secondary]">
                    Total project cost
                  </span>
                  <span className="text-sm">{total} {currency.symbol}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-[--text-dark-secondary]">
                    Amount needed
                  </span>
                  <span className="text-sm">Pending</span>
                </div>
              </div>
              <div className="flex items-start gap-2 rounded-[8px] bg-[#141414] p-4">
                <i
                  className="ri-information-line text-[#36d399]"
                  aria-hidden="true"
                />
                <p
                  id="bank-transfer-pending"
                  className="text-xs leading-relaxed text-[--text-dark-secondary]"
                >
                  Bank transfers and withdrawals are not connected yet. No
                  deposit, payment or currency conversion will be submitted.
                </p>
              </div>
              {/* shortcut: bank processing is undefined, connect this legacy entry after its API is agreed. */}
              <Button
                variant="primary"
                size="lg"
                className="w-full gap-2"
                disabled
                aria-describedby="bank-transfer-pending"
              >
                <i className="ri-bank-line" aria-hidden="true" />
                Start Bank Transfer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
